import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  ADMIN_MATCH_SETTLE,
  AdminMatchSettleVars,
  AdminMatchSettleData,
  matchedColumns,
  missingRequestColumns,
  missingReceiptColumns,
  mismatchColumns,
  totalColumns,
  SEND_CASH_RECEIPT_REMINDER,
  SendCashReceiptReminderVars,
  SendCashReceiptReminderData,
} from "../../../api/settlement-match";
import { notifyError, notify } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";

type TabType =
  | "matched"
  | "missingRequest"
  | "missingReceipt"
  | "mismatch"
  | "total";

export default function AdminSettlementMatch() {
  const [file, setFile] = useState<File | null>(null);
  const [requestDate, setRequestDate] = useState("");
  const [settleWay, setSettleWay] = useState("");
  const [amountTolerance, setAmountTolerance] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<TabType>("matched");
  const [matchData, setMatchData] = useState<
    AdminMatchSettleData["adminMatchSettleWithHometaxXlsx"] | null
  >(null);

  const [mutate, { loading }] = useMutation<
    AdminMatchSettleData,
    AdminMatchSettleVars
  >(ADMIN_MATCH_SETTLE);

  const [sendReminder, { loading: sendingReminder }] = useMutation<
    SendCashReceiptReminderData,
    SendCashReceiptReminderVars
  >(SEND_CASH_RECEIPT_REMINDER);

  const handleSendReminder = async (sellerId: number, amount: number) => {
    try {
      const { data } = await sendReminder({
        variables: {
          sellerId,
          amount,
        },
      });

      if (data?.sendCashReceiptReminder?.ok) {
        notify("문자 전송이 완료되었습니다.");
      } else {
        notifyError(
          data?.sendCashReceiptReminder?.error || "문자 전송에 실패했습니다."
        );
      }
    } catch (error: any) {
      notifyError(error.message || "문자 전송 중 오류가 발생했습니다.");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (
        !selectedFile.name.endsWith(".xlsx") &&
        !selectedFile.name.endsWith(".xls")
      ) {
        notifyError("XLSX 또는 XLS 파일만 업로드 가능합니다.");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      notifyError("파일을 선택해주세요.");
      return;
    }
    if (!requestDate) {
      notifyError("정산요청 접수일을 입력해주세요.");
      return;
    }

    try {
      const { data } = await mutate({
        variables: {
          file,
          requestDate,
          settleWay: settleWay || null,
          amountTolerance: amountTolerance || null,
        },
      });

      if (data?.adminMatchSettleWithHometaxXlsx) {
        setMatchData(data.adminMatchSettleWithHometaxXlsx);
        setActiveTab("matched");
      }
    } catch (error: any) {
      notifyError(error.message || "매칭 처리 중 오류가 발생했습니다.");
    }
  };

  const downloadJSON = () => {
    if (!matchData) return;
    const dataStr = JSON.stringify(matchData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `settlement-match-${requestDate || "result"}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = (type: TabType) => {
    if (!matchData) return;

    let headers: string[] = [];
    let rows: any[] = [];
    // 숫자로 시작하는 필드 (앞의 0이 사라지지 않도록 처리)
    const numericFields = [
      "sellerContactNumber",
      "accountNumber",
      "businessNumber",
    ];

    switch (type) {
      case "matched":
        headers = matchedColumns.map((col) => col.header);
        rows = matchData.matched.map((row) =>
          matchedColumns.map((col) => {
            if (col.render) {
              return col.render(row as any);
            }
            return row[col.key as keyof typeof row] ?? "";
          })
        );
        break;
      case "missingRequest":
        headers = missingRequestColumns.map((col) => col.header);
        rows = matchData.missingRequest.map((row) =>
          missingRequestColumns.map((col) => {
            if (col.render) {
              return col.render(row as any);
            }
            return row[col.key as keyof typeof row] ?? "";
          })
        );
        break;
      case "missingReceipt":
        headers = missingReceiptColumns.map((col) => col.header);
        rows = matchData.missingReceipt.map((row) =>
          missingReceiptColumns.map((col) => {
            if (col.render) {
              return col.render(row as any);
            }
            return row[col.key as keyof typeof row] ?? "";
          })
        );
        break;
      case "mismatch":
        headers = mismatchColumns.map((col) => col.header);
        rows = matchData.mismatch.map((row) =>
          mismatchColumns.map((col) => {
            if (col.render) {
              return col.render(row as any);
            }
            return row[col.key as keyof typeof row] ?? "";
          })
        );
        break;
      case "total":
        headers = totalColumns.map((col) => col.header);
        rows = matchData.total.map((row) =>
          totalColumns.map((col, idx) => {
            let value: any;
            if (col.render) {
              value = col.render(row as any);
            } else {
              value = row[col.key as keyof typeof row] ?? "";
            }
            // 연락처, 계좌번호, 사업자번호 같은 필드는 Excel이 문자열로 인식하도록 처리
            if (numericFields.includes(col.key)) {
              // 원본 값을 가져와서 확인
              const originalValue = row[col.key as keyof typeof row];
              if (
                originalValue &&
                typeof originalValue === "string" &&
                originalValue !== "-" &&
                /^\d+$/.test(originalValue)
              ) {
                // Excel이 문자열로 인식하도록 ="값" 형식으로 반환
                return `="${originalValue}"`;
              }
            }
            return value;
          })
        );
        break;
    }

    const csvContent =
      headers.join(",") +
      "\n" +
      rows
        .map((row) =>
          row
            .map((cell: any) => {
              // 이미 ="값" 형식이면 그대로 사용, 아니면 따옴표로 감싸기
              if (typeof cell === "string" && cell.startsWith('="')) {
                return cell;
              }
              return `"${cell}"`;
            })
            .join(",")
        )
        .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `settlement-${type}-${requestDate || "result"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderTable = <T,>(
    data: T[],
    columns: Array<{
      key: string;
      header: string;
      render?: (row: T) => string | number;
    }>,
    type: TabType
  ) => {
    if (data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">데이터가 없습니다.</div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-4 whitespace-nowrap">
                    {col.render
                      ? col.render(row)
                      : ((row as any)[col.key] ?? "").toString()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">정산 매칭 (홈택스 XLSX)</h1>

      {/* 입력 폼 */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              홈택스 XLSX 파일
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="w-full p-2 border border-gray-300 rounded"
              disabled={loading}
            />
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                선택된 파일: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              정산요청 접수일 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={requestDate}
              onChange={(e) => setRequestDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              정산 방식 (선택)
            </label>
            <input
              type="text"
              value={settleWay}
              onChange={(e) => setSettleWay(e.target.value)}
              placeholder="예: 팝빌"
              className="w-full p-2 border border-gray-300 rounded"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              금액 허용 오차 (원, 선택, 기본값: 0)
            </label>
            <input
              type="number"
              value={amountTolerance}
              onChange={(e) =>
                setAmountTolerance(parseInt(e.target.value) || 0)
              }
              min="0"
              className="w-full p-2 border border-gray-300 rounded"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !file || !requestDate}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? <LoadingSpinnerInline /> : "매칭 실행"}
          </button>
        </form>
      </div>

      {/* 결과 요약 */}
      {matchData && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-bold mb-4">매칭 결과 요약</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-sm text-gray-600">전체 정산</div>
              <div className="text-2xl font-bold text-blue-600">
                {matchData.summary.totalSettles}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded">
              <div className="text-sm text-gray-600">전체 영수증</div>
              <div className="text-2xl font-bold text-green-600">
                {matchData.summary.totalReceipts}
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded">
              <div className="text-sm text-gray-600">매칭 성공</div>
              <div className="text-2xl font-bold text-purple-600">
                {matchData.summary.matched}
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded">
              <div className="text-sm text-gray-600">영수증만 있음</div>
              <div className="text-2xl font-bold text-yellow-600">
                {matchData.summary.missingRequest}
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded">
              <div className="text-sm text-gray-600">정산만 있음</div>
              <div className="text-2xl font-bold text-orange-600">
                {matchData.summary.missingReceipt}
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded">
              <div className="text-sm text-gray-600">금액 불일치</div>
              <div className="text-2xl font-bold text-red-600">
                {matchData.summary.mismatch}
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={downloadJSON}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              전체 결과 JSON 다운로드
            </button>
          </div>
        </div>
      )}

      {/* 탭 및 테이블 */}
      {matchData && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                {
                  key: "matched",
                  label: "매칭 성공",
                  count: matchData.matched.length,
                },
                {
                  key: "missingRequest",
                  label: "영수증만 있음",
                  count: matchData.missingRequest.length,
                },
                {
                  key: "missingReceipt",
                  label: "정산만 있음",
                  count: matchData.missingReceipt.length,
                },
                {
                  key: "mismatch",
                  label: "금액 불일치",
                  count: matchData.mismatch.length,
                },
                {
                  key: "total",
                  label: "전체 데이터",
                  count: matchData.total.length,
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as TabType)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeTab === tab.key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => downloadCSV(activeTab)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                CSV 다운로드
              </button>
            </div>

            {activeTab === "matched" &&
              renderTable(matchData.matched, matchedColumns, "matched")}
            {activeTab === "missingRequest" &&
              renderTable(
                matchData.missingRequest,
                missingRequestColumns,
                "missingRequest"
              )}
            {activeTab === "missingReceipt" && (
              <div>
                {matchData.missingReceipt.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    데이터가 없습니다.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          {missingReceiptColumns.map((col) => (
                            <th
                              key={col.key}
                              className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                            >
                              {col.header}
                            </th>
                          ))}
                          <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            문자 전송
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {matchData.missingReceipt.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {missingReceiptColumns.map((col) => (
                              <td
                                key={col.key}
                                className="px-4 py-4 whitespace-nowrap"
                              >
                                {col.render
                                  ? col.render(row)
                                  : ((row as any)[col.key] ?? "").toString()}
                              </td>
                            ))}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() =>
                                  handleSendReminder(
                                    row.sellerId,
                                    row.totalAmount
                                  )
                                }
                                disabled={sendingReminder}
                                className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                {sendingReminder ? (
                                  <LoadingSpinnerInline />
                                ) : (
                                  "전송"
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {activeTab === "mismatch" &&
              renderTable(matchData.mismatch, mismatchColumns, "mismatch")}
            {activeTab === "total" &&
              renderTable(matchData.total, totalColumns, "total")}
          </div>
        </div>
      )}
    </div>
  );
}
